/**
 * Bootstrap Theme Context
 * Manages Bootstrap-based theme switching with CSS custom properties
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface BootstrapThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  effectiveTheme: 'light' | 'dark'; // The actual applied theme
  toggleTheme: () => void;
}

const BootstrapThemeContext = createContext<BootstrapThemeContextValue | undefined>(undefined);

interface BootstrapThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
  storageKey?: string;
}

export function BootstrapThemeProvider({
  children,
  defaultTheme = 'auto',
  storageKey = 'bootstrap-theme'
}: BootstrapThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored && ['light', 'dark', 'auto'].includes(stored)) {
        return stored as ThemeMode;
      }
    }
    return defaultTheme;
  });

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  // Determine the effective theme based on current theme and system preference
  const getEffectiveTheme = (currentTheme: ThemeMode): 'light' | 'dark' => {
    if (currentTheme === 'auto') {
      if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return currentTheme;
  };

  // Apply theme to DOM
  const applyTheme = (themeToApply: 'light' | 'dark') => {
    if (typeof document !== 'undefined') {
      const html = document.documentElement;
      
      if (themeToApply === 'dark') {
        html.setAttribute('data-bs-theme', 'dark');
        html.classList.add('dark-theme');
        html.classList.remove('light-theme');
      } else {
        html.setAttribute('data-bs-theme', 'light');
        html.classList.add('light-theme');
        html.classList.remove('dark-theme');
      }
    }
    setEffectiveTheme(themeToApply);
  };

  // Set theme and persist to storage
  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, newTheme);
    }
    const effective = getEffectiveTheme(newTheme);
    applyTheme(effective);
  };

  // Toggle between light and dark (auto becomes light)
  const toggleTheme = () => {
    const newTheme = effectiveTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        const effective = e.matches ? 'dark' : 'light';
        applyTheme(effective);
      };

      mediaQuery.addEventListener('change', handleChange);
      
      // Apply initial theme
      const initial = getEffectiveTheme(theme);
      applyTheme(initial);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    } else {
      // Apply theme immediately when not in auto mode
      const effective = getEffectiveTheme(theme);
      applyTheme(effective);
    }
  }, [theme]);

  // Initialize theme on mount
  useEffect(() => {
    const effective = getEffectiveTheme(theme);
    applyTheme(effective);
  }, []);

  const value: BootstrapThemeContextValue = {
    theme,
    setTheme,
    effectiveTheme,
    toggleTheme
  };

  return (
    <BootstrapThemeContext.Provider value={value}>
      {children}
    </BootstrapThemeContext.Provider>
  );
}

export function useBootstrapTheme() {
  const context = useContext(BootstrapThemeContext);
  if (context === undefined) {
    throw new Error('useBootstrapTheme must be used within a BootstrapThemeProvider');
  }
  return context;
}

// Simple hook for just theme toggle functionality
export function useThemeToggle() {
  const { effectiveTheme, toggleTheme } = useBootstrapTheme();
  return { theme: effectiveTheme, toggleTheme };
}